import React, { useState } from "react";
import {useLocation, useNavigate } from "react-router-dom";
import { Activity, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const mobileMenuVariants = {
  hidden: { opacity: 0, height: 0 },
  visible: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.3, ease: "easeInOut" },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.2, ease: "easeInOut" },
  },
};

const NavBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Handler for the “Contact” item
  const handleContactClick = () => {
    if (location.pathname === "/") {
      // If already on the landing page, update URL and scroll to #contact
      setMobileMenuOpen(false);
      window.history.pushState({}, "", "/#contact");
      const contactSection = document.getElementById("contact");
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // If on a different page, navigate to the separate /contact page
      setMobileMenuOpen(false);
      navigate("/contact");
    }
  };

  return (
    <nav className="fixed w-full z-50 bg-gray-900/80 backdrop-blur border-b border-green-500/20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16 items-center">
          {/* Logo/Brand */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="flex items-center space-x-2"
            >
              <Activity className="text-green-400" />
              <span className="text-2xl font-mono font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-500">
                AIORBIS.TECH
              </span>
            </button>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
          <a
              href="/#about"
              className="font-mono text-sm tracking-wider hover:text-green-300 transition-colors relative group"
            >
              <span className="relative z-10">ABOUT</span>
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-green-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
            <a
              href="/#services"
              className="font-mono text-sm tracking-wider hover:text-green-300 transition-colors relative group"
            >
              <span className="relative z-10">SERVICES</span>
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-green-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
            <a
              href="/#projects"
              className="font-mono text-sm tracking-wider hover:text-green-300 transition-colors relative group"
            >
              <span className="relative z-10">PROJECTS</span>
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-green-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
            <a
              href="/#team"
              className="font-mono text-sm tracking-wider hover:text-green-300 transition-colors relative group"
            >
              <span className="relative z-10">OUR TEAM</span>
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-green-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </a>
            <button
              onClick={handleContactClick}
              className="font-mono text-sm tracking-wider hover:text-green-300 transition-colors relative group"
            >
              <span className="relative z-10">CONTACT</span>
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-green-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </button>
          </div>

          {/* Mobile Hamburger Button */}
          <div className="md:hidden">
            {!mobileMenuOpen ? (
              <button
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open Menu"
                className="text-green-400 hover:text-green-300"
              >
                <Menu />
              </button>
            ) : (
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close Menu"
                className="text-green-400 hover:text-green-300"
              >
                <X />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu with Animation */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="md:hidden bg-gray-900/90 border-t border-green-500/20 overflow-hidden"
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="flex flex-col space-y-4 px-4 py-4 font-mono">
            <a
                href="/#about"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm tracking-wider hover:text-green-300 transition-colors"
              >
                ABOUT
              </a>
              <a
                href="/#services"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm tracking-wider hover:text-green-300 transition-colors"
              >
                SERVICES
              </a>
              <a
                href="/#projects"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm tracking-wider hover:text-green-300 transition-colors"
              >
                PROJECTS
              </a>
              <a
                href="/#team"
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm tracking-wider hover:text-green-300 transition-colors"
              >
                OUR TEAM
              </a>
              <button
                onClick={handleContactClick}
                className="text-left text-sm tracking-wider hover:text-green-300 transition-colors"
              >
                CONTACT
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default NavBar;
